import { useEffect, useState, type ReactNode } from "react";

interface CollapsibleProps {
  /** Stable id; doubles as the DOM anchor and the key the open state is remembered under. */
  id: string;
  title: ReactNode;
  hint?: ReactNode;
  /** Shown in the header when collapsed, e.g. a count or a total. */
  summary?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}

/** Dispatch to open (and scroll to) a collapsible section from anywhere. */
export const OPEN_SECTION_EVENT = "cc:open-section";
export const openSection = (id: string) => {
  window.dispatchEvent(new CustomEvent(OPEN_SECTION_EVENT, { detail: id }));
  // Let the body mount before scrolling to it
  requestAnimationFrame(() => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  });
};

const storageKey = (id: string) => `cc:section:${id}`;

const readOpen = (id: string, fallback: boolean): boolean => {
  try {
    const stored = localStorage.getItem(storageKey(id));
    return stored === null ? fallback : stored === "1";
  } catch {
    return fallback;
  }
};

const writeOpen = (id: string, open: boolean) => {
  try {
    localStorage.setItem(storageKey(id), open ? "1" : "0");
  } catch {
    /* private mode or storage disabled: just don't remember */
  }
};

/**
 * A card section whose body can be folded away. The body is not rendered
 * until first opened, so collapsed sections cost no API calls.
 */
export function Collapsible({ id, title, hint, summary, defaultOpen = false, children }: CollapsibleProps) {
  const [open, setOpen] = useState(() => readOpen(id, defaultOpen));
  const [everOpened, setEverOpened] = useState(open);
  const bodyId = `${id}-body`;

  const setOpenAndRemember = (next: boolean) => {
    setOpen(next);
    if (next) setEverOpened(true);
    writeOpen(id, next);
  };

  useEffect(() => {
    const onOpen = (e: Event) => {
      if ((e as CustomEvent<string>).detail === id) setOpenAndRemember(true);
    };
    window.addEventListener(OPEN_SECTION_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SECTION_EVENT, onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <section className="card" id={id} data-section>
      <button
        type="button"
        onClick={() => setOpenAndRemember(!open)}
        aria-expanded={open}
        aria-controls={bodyId}
        className="w-full flex items-start gap-3 text-left group"
      >
        <span
          className={`mt-1.5 shrink-0 text-gray-500 group-hover:text-gray-200 transition-transform ${open ? "rotate-90" : ""}`}
          aria-hidden="true"
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor">
            <path d="M5 2.5l6 5.5-6 5.5z" />
          </svg>
        </span>
        <span className="min-w-0 flex-1">
          <span className="section-title block">{title}</span>
          {hint && open && <span className="section-hint block mt-0.5">{hint}</span>}
        </span>
        {summary && !open && (
          <span className="text-sm text-gray-400 num shrink-0 pt-0.5">{summary}</span>
        )}
      </button>
      {everOpened && (
        <div id={bodyId} className={open ? "mt-4" : "hidden"}>
          {children}
        </div>
      )}
    </section>
  );
}
